import * as React from "react";
import { XCircle, ChevronDown, X, Loader2 } from "lucide-react";
import { cn } from "../lib/utils";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Separator } from "../components/ui/separator";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "../components/ui/command";

export const MultiEntryInput = React.forwardRef(
  (
    {
      value = [],
      onValueChange,
      options = [],
      placeholder = "Type and press Enter",
      maxCount = 3,
      maxSelections, // Add this prop for limiting selections
      className,
      onSearchChange,
      isLoading = false,
      debounceMs = 300,
      showSearch = true,
      ...props
    },
    ref
  ) => {
    const [entries, setEntries] = React.useState(value);
    const [isPopoverOpen, setIsPopoverOpen] = React.useState(false);
    const [inputValue, setInputValue] = React.useState("");
    const [searchValue, setSearchValue] = React.useState("");
    const searchTimeoutRef = React.useRef(null);
    const triggerRef = React.useRef(null);
    const inputRef = React.useRef(null);

    // All hooks must be called unconditionally at the top level
    // Sync with external value changes
    React.useEffect(() => {
      if (value !== undefined) {
        setEntries(value);
      }
    }, [value]);

    // Cleanup timeout on unmount
    React.useEffect(() => {
      return () => {
        if (searchTimeoutRef.current) {
          clearTimeout(searchTimeoutRef.current);
        }
      };
    }, []);

    React.useImperativeHandle(ref, () => ({
      clear: () => {
        setEntries([]);
        onValueChange([]);
      },
      getEntries: () => entries,
    }));

    // Debounced search handler
    const handleSearchChange = React.useCallback((value) => {
      setSearchValue(value);
      setInputValue(value);
      
      if (onSearchChange) {
        // Clear existing timeout
        if (searchTimeoutRef.current) {
          clearTimeout(searchTimeoutRef.current);
        }

        // Set new timeout for debounced search
        searchTimeoutRef.current = setTimeout(() => {
          onSearchChange(value);
        }, debounceMs);
      }
    }, [onSearchChange, debounceMs]);

    const addEntry = React.useCallback((valueToAdd) => {
      // Check maxSelections limit
      if (maxSelections && entries.length >= maxSelections) {
        return; // Don't add if limit reached
      }
      
      if (valueToAdd && !entries.includes(valueToAdd)) {
        const newEntries = [...entries, valueToAdd];
        setEntries(newEntries);
        onValueChange(newEntries);
      }
      setInputValue("");
      setSearchValue("");
    }, [entries, onValueChange, maxSelections]);

    const handleInputKeyDown = React.useCallback((event) => {
      if (event.key === "Enter" && inputValue.trim()) {
        event.preventDefault();
        addEntry(inputValue.trim());
      } else if (event.key === "Backspace" && !inputValue && entries.length > 0) {
        const newEntries = [...entries];
        newEntries.pop();
        setEntries(newEntries);
        onValueChange(newEntries);
      }
    }, [inputValue, entries, onValueChange, addEntry]);

    const handleOptionSelect = React.useCallback((selectedValue) => {
      // Just put the value in the input field, don't add it yet
      setInputValue(selectedValue);
      setSearchValue(selectedValue);
      // Focus the input so user can press Enter immediately
      setTimeout(() => {
        inputRef.current?.focus();
      }, 0);
      // Don't close the popover, let user press Enter to confirm
    }, []);

    const removeEntry = React.useCallback((entryToRemove) => {
      const newEntries = entries.filter((entry) => entry !== entryToRemove);
      setEntries(newEntries);
      onValueChange(newEntries);
    }, [entries, onValueChange]);

    const handleClear = React.useCallback(() => {
      setEntries([]);
      onValueChange([]);
    }, [onValueChange]);

    const handleTogglePopover = React.useCallback(() => {
      setIsPopoverOpen((prev) => !prev);
    }, []);

    return (
      <Popover
        open={isPopoverOpen}
        onOpenChange={setIsPopoverOpen}
      >
        <PopoverTrigger asChild>
          <Button
            ref={(node) => {
              triggerRef.current = node;
              if (typeof ref === 'function') {
                ref(node);
              } else if (ref) {
                ref.current = node;
              }
            }}
            {...props}
            onClick={handleTogglePopover}
            className={cn(
              "flex w-full p-1 rounded-md border min-h-9 h-auto items-center justify-between bg-inherit hover:bg-inherit [&:hover]:bg-inherit",
              entries.length > 0 && "h-auto py-1",
              className
            )}
            style={{ height: 'auto' }}
          >
            {entries.length > 0 ? (
              <div className="flex justify-between items-start w-full gap-2 py-1">
                <div className="flex items-center gap-1 flex-wrap flex-1 min-w-0 my-0.5">
                  {entries.map((entry, index) => (
                    <Badge
                      key={`${entry}-${index}`}
                      variant="accent"
                      className="flex items-center gap-1 px-2 py-0.5 flex-shrink-0"
                    >
                      <span className="text-xs whitespace-nowrap">{entry}</span>
                      <XCircle
                        className="h-3 w-3 cursor-pointer hover:text-destructive flex-shrink-0"
                        onClick={(event) => {
                          event.stopPropagation();
                          removeEntry(entry);
                        }}
                      />
                    </Badge>
                  ))}
                </div>
                <div className="flex items-center gap-1 flex-shrink-0 pt-0.5">
                  <X
                    className="h-4 w-4 cursor-pointer text-muted-foreground hover:text-foreground"
                    onClick={(event) => {
                      event.stopPropagation();
                      handleClear();
                    }}
                  />
                  <Separator
                    orientation="vertical"
                    className="h-4"
                  />
                  <ChevronDown className="h-4 w-4 cursor-pointer text-muted-foreground" />
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-between w-full mx-auto">
                <span className="text-sm text-muted-foreground mx-3">
                  {placeholder}
                </span>
                <ChevronDown className="h-4 cursor-pointer text-muted-foreground mx-2" />
              </div>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className="p-0"
          align="start"
          side="bottom"
          sideOffset={4}
          style={{
            width: triggerRef.current?.offsetWidth || 'auto'
          }}
          onEscapeKeyDown={() => setIsPopoverOpen(false)}
        >
          <Command shouldFilter={false}>
            {showSearch && (
              <CommandInput
                ref={inputRef}
                placeholder={
                  maxSelections && entries.length >= maxSelections
                    ? `Maximum ${maxSelections} entries reached`
                    : "Type or select, then press Enter..."
                }
                value={searchValue}
                onValueChange={handleSearchChange}
                onKeyDown={handleInputKeyDown}
                disabled={maxSelections && entries.length >= maxSelections}
              />
            )}
            <CommandList
              className="max-h-[300px] overflow-y-auto"
              onWheel={(e) => {
                e.stopPropagation();
                const target = e.currentTarget;
                target.scrollTop += e.deltaY;
              }}
            >
              {isLoading ? (
                <div className="flex items-center justify-center py-6">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              ) : (
                <>
                  <CommandEmpty>No results found.</CommandEmpty>
                  
                  {options.length > 0 && (
                    <CommandGroup heading="Available options">
                      {options.map((option) => {
                        const optionValue = typeof option === 'string' ? option : option.value;
                        const optionLabel = typeof option === 'string' ? option : option.label;
                        const isInInput = inputValue === optionValue;
                        const isDisabled = maxSelections && entries.length >= maxSelections;
                        
                        return (
                          <CommandItem
                            key={optionValue}
                            onSelect={() => handleOptionSelect(optionValue)}
                            className={cn(
                              "cursor-pointer",
                              isDisabled && "opacity-50 cursor-not-allowed"
                            )}
                            disabled={isDisabled}
                          >
                            <div className="flex items-center justify-between w-full">
                              <span>{optionLabel}</span>
                              {isInInput && (
                                <Badge variant="outline" className="ml-2 text-xs">
                                  selected
                                </Badge>
                              )}
                            </div>
                          </CommandItem>
                        );
                      })}
                    </CommandGroup>
                  )}
                  
                  <div className="px-2 py-2 border-t bg-muted/50 sticky bottom-0 bg-background">
                    <div className="text-xs text-muted-foreground">
                      <kbd className="px-1.5 py-0.5 text-xs bg-background rounded border">Enter</kbd> to add • <kbd className="px-1.5 py-0.5 text-xs bg-background rounded border">Backspace</kbd> to remove last
                    </div>
                  </div>
                </>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    );
  }
);

MultiEntryInput.displayName = "MultiEntryInput";